// hotkeys/hooks.ts
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useHotkeys as useHotkeysContext } from "./HotkeysProvider";
import { Hotkey, HotkeyScope } from "./types";
import useStore from "../helpers/useStore";
import { CalendarCommands } from "../stores/Calendar/calendar.commands";
import { CalendarContext } from "../stores/Calendar/calendar.provider";

/**
 * Hook for registering hotkeys within a component lifecycle
 */
export const useHotkey = (hotkey: Hotkey) => {
  const { register } = useHotkeysContext();
  const registrationRef = useRef<{ unregister: () => void } | null>(null);

  useEffect(() => {
    registrationRef.current = register(hotkey);

    return () => {
      registrationRef.current?.unregister();
    };
  }, [register, hotkey.combo, hotkey.scope, hotkey.priority]); // Only re-register if key properties change

  return registrationRef.current;
};

/**
 * Hook for registering multiple hotkeys at once
 */
export const useMultipleHotkeys = (hotkeys: Hotkey[]) => {
  const { register } = useHotkeysContext();
  const registrationsRef = useRef<Array<{ unregister: () => void }>>([]);

  useEffect(() => {
    // Unregister previous hotkeys
    registrationsRef.current.forEach((reg) => reg.unregister());

    // Register new hotkeys
    registrationsRef.current = hotkeys.map((hotkey) => register(hotkey));

    return () => {
      registrationsRef.current.forEach((reg) => reg.unregister());
    };
  }, [register, hotkeys]);

  return registrationsRef.current;
};

/**
 * Hook for navigation hotkeys - provides common navigation patterns
 */
export const useNavigationHotkeys = (scope?: HotkeyScope) => {
  const navigate = useNavigate();
  const { register } = useHotkeysContext();
  const CalendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(CalendarStore);
  useEffect(() => {
    const navigationHotkeys = [
      {
        combo: "g d",
        scope: scope || "global",
        description: "Gehe zu: Dashboard",
        handler: () => navigate("/dashboard"),
        priority: 50,
      },
      {
        combo: "g p",
        scope: scope || "global",
        description: "Gehe zu: Patienten",
        handler: () => navigate("/patient"),
        priority: 50,
      },
      // {
      //   combo: "g r",
      //   scope: scope || "global",
      //   description: "Gehe zu: Rezepte",
      //   handler: () => navigate("/receipt"),
      //   priority: 50,
      // },
      {
        combo: "g t",
        scope: scope || "global",
        description: "Gehe zu: Terminkalender",
        handler: () => navigateToCalendar(navigate),
        priority: 50,
      },
      {
        combo: "g a",
        scope: scope || "global",
        description: "Gehe zu: Administration",
        handler: () => navigate("/admin"),
        priority: 50,
      },
      {
        combo: "g s",
        scope: scope || "global",
        description: "Gehe zu: Sales",
        handler: () => navigate("/sales"),
        priority: 50,
      },
      {
        combo: "g h",
        scope: scope || "global",
        description: "Gehe zu: Historie",
        handler: () => navigate("/history"),
        priority: 50,
      },
    ];

    const registrations = navigationHotkeys.map((hotkey) => register(hotkey));

    return () => {
      registrations.forEach((reg) => reg.unregister());
    };
  }, [navigate, register, scope]);
};

/**
 * Hook for scoped hotkeys - automatically manages scope lifecycle
 */
export const useScopedHotkeys = (scope: HotkeyScope, hotkeys: Hotkey[]) => {
  const { register, pushScope } = useHotkeysContext();
  const registrationsRef = useRef<Array<{ unregister: () => void }>>([]);
  const popScopeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // console.debug(`[useScopedHotkeys] Registering ${hotkeys.length} hotkeys for scope: ${scope}`);

    // Push scope
    popScopeRef.current = pushScope(scope);
    // console.debug(`[useScopedHotkeys] Pushed scope: ${scope}`);

    // Register hotkeys with the scope
    const scopedHotkeys = hotkeys.map((hotkey) => ({
      ...hotkey,
      scope: hotkey.scope || scope,
    }));

    // console.debug(
      // `[useScopedHotkeys] Scoped hotkeys:`,
      // scopedHotkeys.map((h) => ({ combo: h.combo, scope: h.scope, description: h.description }))
    // );

    registrationsRef.current = scopedHotkeys.map((hotkey) => register(hotkey));
    // console.debug(`[useScopedHotkeys] Registered ${registrationsRef.current.length} hotkeys`);

    return () => {
      // console.debug(
        // `[useScopedHotkeys] Cleanup: Unregistering ${registrationsRef.current.length} hotkeys for scope: ${scope}`
      // );
      // Cleanup hotkeys first
      registrationsRef.current.forEach((reg) => reg.unregister());
      // Then pop scope
      popScopeRef.current?.();
    };
  }, [register, pushScope, scope, hotkeys]);

  return registrationsRef.current;
};
